// Allowlist of absolute origins that the login form will hard-navigate to
// after a successful login (BE-4115). Kept as an exact-match set rather than
// a regex so a typo in the deploy config can't accidentally widen the
// allowlist. Path is locked to /oauth/authorize because that is the only
// cross-origin redirect target we currently need (the authorization service
// 302s unauthenticated /oauth/authorize requests here with the original URL
// embedded as redirectTo=...).
const ALLOWED_EXTERNAL_ORIGINS: ReadonlySet<string> = new Set([
  'https://identity.staging.rflx.co.uk',
  'https://identity.rflx.co.uk',
  'https://identity.reflexivity.com',
]);

const ALLOWED_EXTERNAL_PATH = '/oauth/authorize';

/**
 * Result of validating a `redirectTo` query parameter against the post-login
 * allowlist. The discriminated `kind` field is the contract call sites use
 * to decide between a full-page navigation (`external` — for cross-origin
 * OAuth flows) and an in-app router navigation (`relative` — the existing
 * /dashboard / /insights / ... behaviour).
 *
 * The shape is intentionally narrow so call sites cannot bypass the
 * allowlist by accident: there is no separate "is this https" helper to
 * misuse — the helper either returns `null` (no redirect) or one of the
 * two safe shapes.
 */
export type SafePostLoginRedirect =
  | { kind: 'relative'; href: string }
  | { kind: 'external'; href: string }
  | null;

/**
 * Returns a safe value for `redirectTo` after a successful login, or `null`
 * when the input should be ignored.
 *
 * Behaviour matrix:
 * - `null` / empty input          -> `null` (no redirect)
 * - relative path (`/foo`)        -> `{ kind: 'relative', href: '/foo' }`
 *                                    (existing in-app behaviour; call sites
 *                                    route this through useNavigate)
 * - absolute https URL matching
 *   the allowlisted origin AND
 *   `/oauth/authorize` path        -> `{ kind: 'external', href: <canonical> }`
 *                                     (intended for `window.location.href` —
 *                                     full-page nav so cross-origin cookies
 *                                     are set)
 * - anything else                  -> `null` (rejected: http://,
 *                                     protocol-relative `//host`, userinfo,
 *                                     wrong origin, wrong path,
 *                                     `javascript:`, malformed URLs)
 *
 * This is the single source of truth that both `LoginForm` and
 * `AppHistory` consult so the two code paths cannot disagree on what
 * counts as a safe redirect target.
 */
export const getSafePostLoginRedirect = (
  raw: string | null | undefined
): SafePostLoginRedirect => {
  if (!raw) return null;

  // Relative paths (but NOT protocol-relative `//host/...`) are preserved
  // for the existing in-app redirect flow. The leading-slash check rejects
  // `//evil.com` which the URL parser would treat as absolute.
  if (raw.startsWith('/') && !raw.startsWith('//')) {
    return { kind: 'relative', href: raw };
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:') return null;
  if (parsed.username || parsed.password) return null;
  if (!ALLOWED_EXTERNAL_ORIGINS.has(parsed.origin)) return null;
  if (parsed.pathname !== ALLOWED_EXTERNAL_PATH) return null;

  return { kind: 'external', href: parsed.toString() };
};
